import { useState, type ReactNode } from "react";
import { KeyRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/feedback";
import { Checkbox, RadioGroup, Textarea } from "@/components/ui/form-controls";
import { Select } from "@/components/ui/select";
import { UnavailableValue } from "@/components/data-display/unavailable-value";
import { WipHint, WIP_ACTION_HINT } from "@/components/ui/wip-hint";
import { cn } from "@/lib/utils";

/**
 * RCV-02 — Recovery Request Creation.
 *
 * Layout ported from `.design-ref/prototype/app/(enterprise-wallet)/recovery/request/
 * page.tsx` → `features/recovery/recovery-request.tsx`: the page header, the
 * three-step wizard header, and the two wizard steps (request details, then notify
 * custodians).
 *
 * No data and no behaviour are ported. The design read its option lists from
 * `data/mock.ts` (`RECOVERY_REQUEST_CUSTODIANS`, `RECOVERY_AFFECTED_WALLET_OPTIONS`,
 * `RECOVERY_REQUEST_DEFAULTS.messagePreview`) — fictional wallets and custodians —
 * and submitted through a client-side store. There is no recovery backend in this
 * environment (page-delivery-pipeline.md G2 `defer`), and this is a custody-adjacent
 * ceremony, so nothing here may render a fabricated request, custodian or key state.
 *
 * What is carried over is the design's structure and classes: the step header, the
 * field captions, the two wizard cards, and the option *labels* for urgency and
 * notification method (UI copy). Every control that would need the missing backend
 * is `disabled`, carries `WIP_ACTION_HINT` through `title` and `aria-describedby`,
 * and has no handler that could report success. The one control that does not need
 * the backend — the step navigation — stays live, and the design's own Step 3
 * ("Review & Submit") is a permanently disabled stub in the design and stays one.
 */

/** Id of the paragraph that states, in plain text, why the controls are inert. */
const ACTION_HINT_ID = "recovery-request-action-hint";

/**
 * A labelled form field — the design's local `Field` helper. The caption is a real
 * `<label for>` when the field has a focusable control (so it is announced as the
 * control's name) and a `<span>` for grouped controls, which name themselves.
 */
function Field({
  label,
  htmlFor,
  children,
}: {
  label: string;
  htmlFor?: string;
  children: ReactNode;
}) {
  const captionClass = "mb-1.5 block text-xs font-semibold uppercase tracking-wide text-muted";
  return (
    <div>
      {htmlFor ? (
        <label htmlFor={htmlFor} className={captionClass}>
          {label}
        </label>
      ) : (
        <span className={captionClass}>{label}</span>
      )}
      {children}
    </div>
  );
}

/** The design's wizard tab. Step 3 is disabled in the design and stays disabled. */
function StepTab({
  label,
  active,
  disabled,
  onClick,
}: {
  label: string;
  active: boolean;
  disabled?: boolean;
  onClick?: () => void;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      aria-current={active ? "step" : undefined}
      className={cn(
        "rounded-[6px] border px-3 py-1.5 text-xs font-semibold",
        active
          ? "border-primary bg-primary text-on-primary"
          : disabled
            ? "cursor-not-allowed border-line bg-disabled-soft text-disabled"
            : "border-line bg-surface text-muted hover:bg-bg",
      )}
    >
      {label}
    </button>
  );
}

/** Props every control that needs the missing backend carries. */
const inert = {
  disabled: true,
  title: WIP_ACTION_HINT,
  "aria-describedby": ACTION_HINT_ID,
} as const;

export default function RecoveryRequestPage() {
  const [step, setStep] = useState<1 | 2>(1);

  return (
    <div data-testid="recovery-request-page">
      <div className="mb-5">
        <div className="flex flex-wrap items-center gap-2">
          <h1 className="text-[24px] font-bold leading-tight tracking-tight text-ink">
            Initiate Recovery Request
          </h1>
          <WipHint />
        </div>
        <p className="mt-1 text-sm text-muted">Submit a new key recovery request for custodian approval.</p>
        <p className="mt-1 text-sm text-muted" data-testid="wip-note">
          This area has no backend in this environment yet.
        </p>
      </div>

      <div className="mb-6 flex flex-wrap gap-1.5">
        <StepTab label="1. Request Details" active={step === 1} onClick={() => setStep(1)} />
        <StepTab label="2. Notify Custodians" active={step === 2} onClick={() => setStep(2)} />
        {/* Permanently disabled in the design itself: step 3 was never wired. */}
        <StepTab label="3. Review & Submit" active={false} disabled />
      </div>

      {step === 1 && (
        <div className="max-w-xl rounded-[10px] border border-line bg-surface p-6">
          <div className="space-y-4">
            <Field label="Recovery Type" htmlFor="recovery-type">
              <Select id="recovery-type" defaultValue="" className="w-full" {...inert}>
                <option value="">Not available yet</option>
              </Select>
            </Field>

            <Field label="Reason" htmlFor="recovery-reason">
              <Textarea
                id="recovery-reason"
                rows={3}
                defaultValue=""
                placeholder="Not available yet"
                className="w-full"
                {...inert}
              />
            </Field>

            <Field label="Affected Wallet" htmlFor="recovery-wallet">
              <Select id="recovery-wallet" defaultValue="" className="w-full" {...inert}>
                <option value="">Not available yet</option>
              </Select>
            </Field>

            {/* `RadioGroup` has no `disabled` prop, so the group is disabled the
                standard way — a disabled `fieldset`, which disables every
                descendant form control. */}
            <Field label="Urgency">
              <fieldset disabled title={WIP_ACTION_HINT} aria-describedby={ACTION_HINT_ID} className="m-0 min-w-0 border-0 p-0">
                <RadioGroup
                  ariaLabel="Urgency"
                  value="normal"
                  onChange={() => undefined}
                  options={[
                    { value: "normal", label: "Normal" },
                    { value: "elevated", label: "Elevated" },
                    { value: "emergency", label: "Emergency" },
                  ]}
                />
              </fieldset>
            </Field>

            <div className="flex items-start gap-2.5 text-sm text-ink" title={WIP_ACTION_HINT}>
              <Checkbox
                checked={false}
                onCheckedChange={() => undefined}
                ariaLabel="I confirm this recovery request is authorized and accurate."
                disabled
              />
              <span>I confirm this recovery request is authorized and accurate.</span>
            </div>
          </div>

          <Button className="mt-5" {...inert}>
            Continue →
          </Button>
        </div>
      )}

      {step === 2 && (
        <div className="max-w-xl rounded-[10px] border border-line bg-surface p-6">
          <div className="mb-4 text-sm font-bold text-ink">Notify Custodians</div>

          {/* The design renders one checkbox per custodian, read from
              `RECOVERY_REQUEST_CUSTODIANS` in `data/mock.ts`. That roster is
              fictional, so the designed empty state stands in for it. */}
          <EmptyState
            className="px-0 pt-0 pb-6"
            icon={<KeyRound aria-hidden className="text-muted" />}
            title="No custodians to notify yet"
            description="Custodians for a recovery request come from the recovery service, which does not exist in this environment yet."
          />

          <Field label="Notification Method">
            <fieldset disabled title={WIP_ACTION_HINT} aria-describedby={ACTION_HINT_ID} className="m-0 min-w-0 border-0 p-0">
              <RadioGroup
                ariaLabel="Notification Method"
                value="in-app"
                onChange={() => undefined}
                options={[
                  { value: "in-app", label: "In-app" },
                  { value: "email", label: "Email" },
                  { value: "both", label: "Both" },
                ]}
              />
            </fieldset>
          </Field>

          <div
            className="mt-4 rounded-md border border-line bg-bg p-3 text-xs italic text-muted"
            data-testid="notification-preview"
          >
            Notification preview: <UnavailableValue />
          </div>

          <div className="mt-5 flex gap-2.5">
            <Button variant="secondary" onClick={() => setStep(1)}>
              ← Back
            </Button>
            <Button {...inert}>Submit for Approval →</Button>
          </div>
        </div>
      )}

      {/* Why every control above is inert, stated in plain text and referenced by
          each control's `aria-describedby`. */}
      <p id={ACTION_HINT_ID} className="mt-4 text-xs text-muted" data-testid="wip-action-hint">
        {WIP_ACTION_HINT}. Nothing on this page is submitted anywhere.
      </p>
    </div>
  );
}
